import {
  useMemo,
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'
import {IconButton, Box, Tooltip, Typography} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import EditRounded from '@mui/icons-material/EditRounded'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setActiveScene,
  selectActiveSceneIndex,
  selectSceneCount,
  selectSceneNames,
} from '../../../common/components/store/scenesSlice'
import {switchSceneHistory} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {useTranslation} from 'react-i18next'
import useSceneDialogs from './useSceneDialogs'

// ── SceneToolbar — top bar with tab-style scene selector ──────────

const SceneToolbar = () => {
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const {t} = useTranslation()
  const activeIndex = useAppSelector(selectActiveSceneIndex)
  const sceneCount = useAppSelector(selectSceneCount)
  const sceneNames = useAppSelector(selectSceneNames)
  const {openAdd, openRename, openRemove, dialogs} = useSceneDialogs()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Translate vertical mouse-wheel into horizontal scroll (touchpad
  // horizontal gestures already scroll natively via overflowX: auto).
  const handleWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
    el.scrollLeft += e.deltaY
  }, [])

  const handleSetActive = useCallback(
    (index: number) => {
      if (index === activeIndex) return
      dispatch(switchSceneHistory({fromScene: activeIndex, toScene: index}))
      dispatch(setActiveScene(index))
    },
    [dispatch, activeIndex],
  )

  const handleCloseClick = useCallback(
    (e: ReactMouseEvent, index: number) => {
      e.stopPropagation()
      openRemove(index)
    },
    [openRemove],
  )

  const handleRenameClick = useCallback(
    (e: ReactMouseEvent, index: number) => {
      e.stopPropagation()
      openRename(index)
    },
    [openRename],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <>
      <Box sx={sx.root}>
        <Box sx={sx.tabRow}>
          <Box sx={sx.scrollWrap}>
            <Box ref={scrollRef} sx={sx.tabScroll} onWheel={handleWheel}>
              {Array.from({length: sceneCount}, (_, idx) => (
                <Box key={idx} sx={idx === activeIndex ? sx.tabActive : sx.tab} onClick={() => handleSetActive(idx)}>
                  <Typography sx={sx.label}>{sceneNames[idx] ?? `Scene ${idx + 1}`}</Typography>
                  <Tooltip title={t('toolbar.renameScene')} placement="bottom">
                    <IconButton
                      size="small"
                      sx={sx.renameBtn}
                      onClick={(e) => handleRenameClick(e, idx)}
                      aria-label={`${t('toolbar.renameScene')} ${sceneNames[idx] ?? `Scene ${idx + 1}`}`}
                    >
                      <EditRounded sx={sx.closeIcon} />
                    </IconButton>
                  </Tooltip>
                  {sceneCount > 1 && (
                    <Tooltip title={t('toolbar.removeScene')} placement="bottom">
                      <IconButton
                        size="small"
                        sx={sx.closeBtn}
                        onClick={(e) => handleCloseClick(e, idx)}
                        aria-label={`${t('toolbar.removeScene')} ${sceneNames[idx] ?? `Scene ${idx + 1}`}`}
                      >
                        <CloseRounded sx={sx.closeIcon} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={sx.fade} />
          </Box>
          <Tooltip title={t('toolbar.addScene')} placement="bottom">
            <IconButton size="small" onClick={openAdd} sx={sx.addBtn}>
              <AddRounded sx={sx.addIcon} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {dialogs}
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    width: '100%',
    backgroundColor: tokens.sidebar.background,
    borderBottom: `1px solid ${tokens.sidebar.border}`,
    flexShrink: 0,
  },
  tabRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1px',
    padding: '6px 8px 0',
    minHeight: 46,
  },
  scrollWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
  },
  fade: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: '28px',
    pointerEvents: 'none',
    background: `linear-gradient(to right, rgba(0, 0, 0, 0), ${tokens.sidebar.background})`,
  },
  tabScroll: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1px',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    minWidth: 0,
    // Hide the scrollbar so it doesn't reserve vertical space and shift
    // the toolbar height; scrolling still works via touchpad + wheel.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '7px 14px',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    flexShrink: 0,
    color: tokens.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: tokens.text.primary,
    },
  },
  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '7px 14px',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderBottom: `2px solid ${tokens.accent.primary}`,
    marginBottom: '-1px',
    flexShrink: 0,
    color: tokens.text.primary,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  renameBtn: {
    padding: '1px',
    marginLeft: '2px',
    color: tokens.text.muted,
    opacity: 0.5,
    '&:hover': {
      opacity: 1,
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  closeBtn: {
    padding: '1px',
    marginRight: '-4px',
    color: tokens.text.muted,
    '&:hover': {
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 80, 80, 0.25)',
    },
  },
  closeIcon: {
    fontSize: 15,
  },
  addBtn: {
    color: tokens.text.muted,
    padding: '4px',
    marginBottom: '2px',
    marginLeft: '2px',
    flexShrink: 0,
    '&:hover': {
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  addIcon: {
    fontSize: 19,
  },
})

export default SceneToolbar
